import { GreenhouseScheduledInterviewFragment } from "../admin/graphql";
import { ScheduledInterviewer, User } from "../main/graphql";

// Current possible values from GH:

// definitely_not, no, yes, strong_yes, no_decision
export const GH_RATING_DEFINITELY_NOT = "definitely_not";
export const GH_RATING_NO = "no";
export const GH_RATING_YES = "yes";
export const GH_RATING_STRONG_YES = "strong_yes";
export const GH_RATING_NO_DECISION = "no_decision";

export const formatGreenhouseRating = (rating: string): string => {
  switch (rating) {
    case GH_RATING_DEFINITELY_NOT:
      return "Definitely not";
    case GH_RATING_NO:
      return "No";
    case GH_RATING_YES:
      return "Yes";
    case GH_RATING_STRONG_YES:
      return "Strong yes";
    case GH_RATING_NO_DECISION:
      return "No decision";
    default:
      return "Unknown rating";
  }
};

// Restore as type alias
type ScheduledInterviewerPreview = Pick<ScheduledInterviewer, "id"> & {
  user: Pick<User, "id" | "email" | "fullName" | "greenhouseId">;
};

export interface GreenhouseRatingSummaryItem {
  ratingKey: string;
  count: number;
  formattedRating: string;
}

export const summarizeGreenhouseRatings = (
  ratings: ReadonlyArray<string | null> | null | undefined
): GreenhouseRatingSummaryItem[] | null => {
  const validRatings = (ratings?.filter((r) => r !== null) as string[]) || [];

  if (validRatings.length === 0) {
    // Return null instead of a string when no ratings are submitted
    return null;
  }

  // Use reduce to count ratings
  const ratingCounts = validRatings.reduce((acc, rating) => {
    acc[rating] = (acc[rating] || 0) + 1;
    return acc;
  }, {} as Record<string, number>); // Initialize with specific type

  const ratingOrder = [
    GH_RATING_DEFINITELY_NOT,
    GH_RATING_NO,
    GH_RATING_NO_DECISION,
    GH_RATING_YES,
    GH_RATING_STRONG_YES,
  ];

  const summaryItems: GreenhouseRatingSummaryItem[] = [];

  // Use forEach for ordered ratings
  ratingOrder.forEach((ratingKey) => {
    const count = ratingCounts[ratingKey];
    summaryItems.push({
      ratingKey,
      count,
      formattedRating: formatGreenhouseRating(ratingKey),
    });
  });

  // Handle unexpected ratings (add them to the summary without specific order)
  Object.keys(ratingCounts).forEach((ratingKey) => {
    if (!ratingOrder.includes(ratingKey)) {
      const count = ratingCounts[ratingKey];
      summaryItems.push({
        ratingKey,
        count,
        formattedRating: formatGreenhouseRating(ratingKey),
      });
    }
  });

  return summaryItems.length > 0 ? summaryItems : null;
};

export const getGreenhouseInterviewers = (
  greenhouseInterview?: GreenhouseScheduledInterviewFragment | null,
  scheduledInterviewers?: ScheduledInterviewerPreview[]
): {
  email: string;
  id: number;
}[] => {
  const rawGreenhouseInterview = greenhouseInterview?.interview
    ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      JSON.parse(greenhouseInterview?.interview)
    : null;
  let rawGreenhouseInterviewers = (rawGreenhouseInterview?.interviewers ??
    []) as { email: string; id: number }[];
  rawGreenhouseInterviewers = rawGreenhouseInterviewers.filter((rgi) => {
    let found = false;
    scheduledInterviewers?.forEach((si) => {
      if (si.user.greenhouseId === rgi.id) found = true;
    });
    return !found;
  });
  return rawGreenhouseInterviewers;
};
